"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import {
  FolderOpen, Users, FileText, X, Check, ArrowRight, Sparkles,
} from "lucide-react";
import { useOnboarding } from "@/lib/use-onboarding";
import { useWorkspace } from "@/lib/workspace-context";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/toast";

interface OnboardingGuideProps {
  state: ReturnType<typeof useOnboarding>;
}

export function OnboardingGuide({ state }: OnboardingGuideProps) {
  const router = useRouter();
  const { createFile, openFile } = useWorkspace();

  if (!state.shouldShow) return null;

  const steps = [
    {
      n: 1,
      title: "Crea tu primer proyecto",
      desc: "Organiza documentos y tareas por proyecto o equipo.",
      icon: <FolderOpen className="w-4 h-4" strokeWidth={1.75} />,
      done: state.hasProjects,
      action: () => {
        router.push("/workspace/spaces");
        state.markStepDone(1);
      },
    },
    {
      n: 2,
      title: "Invita a tu equipo",
      desc: "Añade miembros para colaborar en documentos y asignar tareas.",
      icon: <Users className="w-4 h-4" strokeWidth={1.75} />,
      done: state.hasMembers,
      action: () => {
        router.push("/workspace/users");
        state.markStepDone(2);
      },
    },
    {
      n: 3,
      title: "Crea tu primer documento",
      desc: "Escribe con Markdown y enlaza notas con [[Título]].",
      icon: <FileText className="w-4 h-4" strokeWidth={1.75} />,
      done: state.hasDocuments,
      action: () => {
        const id = createFile("Bienvenida a Arsenal Wiki");
        openFile(id);
        state.markStepDone(3);
        toast.success("Documento creado", "Empieza a escribir tu primera nota.");
      },
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="w-full rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card shadow-sm p-4 sm:p-5"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.75} />
            <span className="text-xs font-semibold text-foreground">
              Primeros pasos en tu workspace
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold tabular-nums">
              {doneCount}/3 completados
            </span>
          </div>
          <button
            onClick={state.dismiss}
            className="p-1 rounded-md text-muted-foreground/60 hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Omitir guía"
            title="Ocultar guía de inicio"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {steps.map((s) => (
            <button
              key={s.n}
              onClick={s.action}
              disabled={s.done}
              className={cn(
                "w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all",
                s.done
                  ? "bg-success/8 opacity-70 cursor-default"
                  : "hover:bg-secondary/50 active:scale-[0.99]"
              )}
            >
              <div
                className={cn(
                  "w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-semibold transition-colors",
                  s.done
                    ? "bg-success/15 text-success border border-success/30"
                    : "bg-primary/10 text-primary border border-primary/20"
                )}
              >
                {s.done ? (
                  <Check className="w-3.5 h-3.5" strokeWidth={2} />
                ) : (
                  s.n
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    "text-xs font-medium leading-tight",
                    s.done ? "text-muted-foreground line-through" : "text-foreground"
                  )}
                >
                  {s.title}
                </p>
                <p className="text-[10px] text-muted-foreground/70 leading-snug mt-0.5 text-pretty">
                  {s.desc}
                </p>
              </div>
              {!s.done && (
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-border/30 flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground/60">
            {doneCount === 3 ? "Listo" : "Tómate tu tiempo"}
          </span>
          <button
            onClick={state.dismiss}
            className="text-[10px] text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Omitir guía
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}